<template>
  <div v-if="isVisible" class="quick-shipper-invoice-qty-modal-root">
    <div class="modal-backdrop fade show" @click="close"></div>
    <div class="modal fade show" tabindex="-1" @click.self="close">
        <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="fas fa-weight-hanging me-2 text-danger" aria-hidden="true"></i>
              {{ $t("financialSummary.stages.shipPaymentsEditShipperInvoiceQtyTitle") }}
            </h5>
            <button type="button" class="btn-close" aria-label="Close" @click="close"></button>
          </div>
          <div class="modal-body">
            <p class="text-muted small mb-3">
              {{ $t("financialSummary.stages.shipPaymentsEditShipperInvoiceQtyHint") }}
            </p>
            <div v-if="shippingName" class="small fw-semibold text-body mb-2">
              {{ shippingName }}
            </div>
            <ContentSkeleton v-if="loading" variant="table" :rows="4" :cols="6" padded />
            <div v-else-if="loadError" class="alert alert-danger mb-0">
              {{ loadError }}
            </div>
            <div v-else-if="!editableRows.length" class="alert alert-light mb-0">
              {{ $t("financialSummary.stages.shipPaymentsEditShipperInvoiceQtyEmpty") }}
            </div>
            <div v-else class="table-responsive">
              <table class="table table-sm table-bordered align-middle mb-0">
                <thead class="table-light">
                  <tr>
                    <th scope="col">{{ $t("financialSummary.stages.shipPaymentsEditShipperPricesProduct") }}</th>
                    <th scope="col" class="text-end" style="width: 9rem">
                      {{ $t("financialSummary.stages.shipPaymentsEditShipperInvoiceQtyClientRef") }}
                    </th>
                    <th scope="col" class="text-end" style="width: 10rem">
                      {{ $t("financialSummary.stages.shipPaymentsEditShipperInvoiceQtySupplierCol") }}
                    </th>
                    <th scope="col" class="text-end" style="width: 8rem">
                      {{ $t("financialSummary.stages.shipPaymentsEditShipperPricesInvoiceUnitPrice") }}
                    </th>
                    <th scope="col" class="text-end" style="width: 9rem">
                      {{ $t("financialSummary.stages.shipPaymentsEditShipperPricesUnitPrice") }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in editableRows" :key="row.key">
                    <td class="small">{{ row.productLabel }}</td>
                    <td class="text-end small text-muted fw-semibold tabular-nums">
                      {{ formatQtyRefCell(row.clientInvoiceUnitQtyRef) }}
                    </td>
                    <td class="text-end">
                      <input
                        v-model="row.unitQtyDraft"
                        type="text"
                        inputmode="decimal"
                        class="form-control form-control-sm text-end"
                        autocomplete="off"
                      />
                    </td>
                    <td class="text-end small text-primary fw-semibold tabular-nums">
                      {{ formatMoneyCell(row.invoiceSaleUnitPrice) }}
                    </td>
                    <td class="text-end">
                      <input
                        v-model="row.unitPriceDraft"
                        type="text"
                        inputmode="decimal"
                        class="form-control form-control-sm text-end"
                        autocomplete="off"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="close">
              {{ $t("cancel") }}
            </button>
            <button
              type="button"
              class="btn btn-primary"
              :disabled="loading || saving || !editableRows.length || !!loadError"
              @click="submit"
            >
              <span
                v-if="saving"
                class="spinner-border spinner-border-sm me-1"
                role="status"
                aria-hidden="true"
              ></span>
              {{ $t("financialSummary.stages.shipPaymentsEditShipperInvoiceQtySave") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { wencesApi } from "@/api/wencesApi";
import Swal from "sweetalert2";
import { formatNumberSync } from "@/utils/numberFormatter";
import ContentSkeleton from "@/components/common/ContentSkeleton.vue";

function productRefToId(product) {
  if (product == null || product === "") return null;
  if (typeof product === "object" && product._id != null) return String(product._id);
  return String(product);
}

function looksLikeMongoObjectId(s) {
  return typeof s === "string" && /^[a-f0-9]{24}$/i.test(s);
}

function displayFromProductRef(product) {
  if (product == null || product === "") return { productId: null, productLabel: "—" };
  if (typeof product === "object" && product._id != null) {
    const id = String(product._id);
    const named =
      (product.name && String(product.name).trim()) ||
      (product.label && String(product.label).trim()) ||
      (product.title && String(product.title).trim()) ||
      (product.sku && String(product.sku).trim()) ||
      "";
    return { productId: id, productLabel: named || id };
  }
  const id = String(product).trim();
  if (!id) return { productId: null, productLabel: "—" };
  return { productId: id, productLabel: id };
}

/** `productandprice[].products[].unit_qty` en el mismo embarque (factura cliente), por producto. */
function buildClientInvoiceUnitQtyByProductId(shippingData) {
  const map = new Map();
  const pp = shippingData && shippingData.productandprice;
  if (!Array.isArray(pp)) return map;
  for (const block of pp) {
    if (!block || typeof block !== "object") continue;
    const products = Array.isArray(block.products) ? block.products : [];
    for (const line of products) {
      const pid = productRefToId(line.product);
      if (!pid) continue;
      const raw = line.unit_qty;
      if (raw === null || raw === undefined || raw === "") continue;
      const n = Number(raw);
      if (Number.isFinite(n)) map.set(pid, n);
    }
  }
  return map;
}

/** `productandprice[].products[].unit_price` (venta / factura cliente), por id de producto. */
function buildInvoiceSaleUnitPriceByProductId(shippingData) {
  const map = new Map();
  const pp = shippingData && shippingData.productandprice;
  if (!Array.isArray(pp)) return map;
  for (const block of pp) {
    if (!block || typeof block !== "object") continue;
    const products = Array.isArray(block.products) ? block.products : [];
    for (const line of products) {
      const pid = productRefToId(line.product);
      if (!pid) continue;
      const raw = line.unit_price;
      if (raw === null || raw === undefined || raw === "") continue;
      const n = Number(raw);
      if (Number.isFinite(n)) map.set(pid, n);
    }
  }
  return map;
}

function parseQtyDraft(raw) {
  if (raw === "" || raw === null || raw === undefined) return null;
  const n = parseFloat(String(raw).replace(",", ".").trim());
  return Number.isFinite(n) ? n : NaN;
}

function parsePriceDraft(raw) {
  if (raw === "" || raw === null || raw === undefined) return null;
  const n = parseFloat(String(raw).replace(",", ".").trim());
  return Number.isFinite(n) ? n : NaN;
}

export default {
  name: "QuickShipperInvoiceUnitQtyModal",
  components: { ContentSkeleton },
  props: {
    isVisible: { type: Boolean, default: false },
    shippingId: { type: String, default: "" },
    shippingName: { type: String, default: "" },
  },
  data() {
    return {
      loading: false,
      saving: false,
      loadError: "",
      shippingDoc: null,
      editableRows: [],
    };
  },
  watch: {
    isVisible(val) {
      if (val && this.shippingId) {
        this.loadShipping();
      }
      if (!val) {
        this.resetLocal();
      }
    },
    shippingId() {
      if (this.isVisible && this.shippingId) {
        this.loadShipping();
      }
    },
  },
  methods: {
    formatQtyRefCell(value) {
      if (value === null || value === undefined || value === "") return "—";
      const n = Number(value);
      if (!Number.isFinite(n)) return "—";
      return formatNumberSync(n, null, true);
    },
    formatMoneyCell(value) {
      if (value === null || value === undefined || value === "") return "—";
      const n = Number(value);
      if (!Number.isFinite(n)) return "—";
      return "$" + formatNumberSync(n, null, true);
    },
    resetLocal() {
      this.loadError = "";
      this.shippingDoc = null;
      this.editableRows = [];
    },
    close() {
      this.$emit("close");
    },
    pickLabelFromProductDoc(doc) {
      if (!doc || typeof doc !== "object") return null;
      const n =
        (doc.name && String(doc.name).trim()) ||
        (doc.label && String(doc.label).trim()) ||
        (doc.title && String(doc.title).trim()) ||
        (doc.sku && String(doc.sku).trim()) ||
        "";
      return n || null;
    },
    async fetchProductLabelById(productId) {
      if (!looksLikeMongoObjectId(productId)) return null;
      const candidates = ["products", "product", "Products", "Product"];
      for (const model of candidates) {
        try {
          const { data } = await wencesApi.get(
            `/api/gotrading/dynamic/${encodeURIComponent(model)}/${encodeURIComponent(productId)}`
          );
          if (data && data._id) {
            const label = this.pickLabelFromProductDoc(data);
            if (label) return label;
          }
        } catch (e) {
          const st = e.response && e.response.status;
          if (st === 404 || st === 400) continue;
        }
      }
      return null;
    },
    async enrichRowsProductNames(rows) {
      const ids = [
        ...new Set(rows.map((r) => r.productId).filter((id) => id && looksLikeMongoObjectId(String(id)))),
      ];
      const cache = Object.create(null);
      await Promise.all(
        ids.map(async (id) => {
          const sample = rows.find((r) => String(r.productId) === String(id));
          const alreadyHuman =
            sample &&
            String(sample.productLabel).trim() &&
            String(sample.productLabel) !== String(id);
          if (alreadyHuman) {
            cache[id] = String(sample.productLabel).trim();
            return;
          }
          const fetched = await this.fetchProductLabelById(String(id));
          cache[id] = fetched || String(id);
        })
      );
      rows.forEach((row) => {
        if (!row.productId) return;
        const key = String(row.productId);
        if (cache[key] != null) {
          this.$set(row, "productLabel", cache[key]);
        }
      });
    },
    async loadShipping() {
      this.loading = true;
      this.loadError = "";
      this.shippingDoc = null;
      this.editableRows = [];
      try {
        const { data } = await wencesApi.get(`/api/gotrading/dynamic/shipping/${this.shippingId}`);
        if (!data || !data._id) {
          this.loadError = this.$t("financialSummary.stages.shipPaymentsEditShipperInvoiceQtyLoadError");
          return;
        }
        this.shippingDoc = data;
        const clientQtyByProduct = buildClientInvoiceUnitQtyByProductId(data);
        const invoiceSaleByProduct = buildInvoiceSaleUnitPriceByProductId(data);
        const pps = data.productandpriceshipper;
        const blocks = Array.isArray(pps) ? pps : [];
        const rows = [];
        blocks.forEach((block, blockIdx) => {
          if (!block || !block._id) return;
          const products = Array.isArray(block.products) ? block.products : [];
          products.forEach((p, productIdx) => {
            const uq = p.unit_qty;
            const draft =
              uq === null || uq === undefined || uq === "" ? "" : String(Number(uq));
            const up = p.unit_price;
            const priceDraft =
              up === null || up === undefined || up === "" ? "" : String(Number(up));
            const { productId, productLabel } = displayFromProductRef(p.product);
            const pidKey = productId ? String(productId) : null;
            const clientInvoiceUnitQtyRef =
              pidKey && clientQtyByProduct.has(pidKey) ? clientQtyByProduct.get(pidKey) : null;
            const invoiceSaleUnitPrice =
              pidKey && invoiceSaleByProduct.has(pidKey)
                ? invoiceSaleByProduct.get(pidKey)
                : null;
            rows.push({
              key: `${String(block._id)}-${productIdx}`,
              groupId: String(block._id),
              blockIndex: blockIdx,
              productIndex: productIdx,
              productId,
              productLabel,
              clientInvoiceUnitQtyRef,
              invoiceSaleUnitPrice,
              unitQtyDraft: draft,
              unitPriceDraft: priceDraft,
            });
          });
        });
        await this.enrichRowsProductNames(rows);
        this.editableRows = rows;
      } catch (e) {
        console.error("QuickShipperInvoiceUnitQtyModal loadShipping:", e);
        const msg =
          (e.response && e.response.data && (e.response.data.message || e.response.data.error)) ||
          e.message ||
          "";
        this.loadError = msg || this.$t("financialSummary.stages.shipPaymentsEditShipperInvoiceQtyLoadError");
      } finally {
        this.loading = false;
      }
    },
    sanitizeGroupBlockTopLevel(block) {
      const out = { ...block };
      for (const k of Object.keys(out)) {
        if (k === "products") continue;
        const v = out[k];
        if (v && typeof v === "object" && !Array.isArray(v) && v._id != null && !(v instanceof Date)) {
          out[k] = v._id;
        }
      }
      return out;
    },
    buildProductandpriceshipperPayload() {
      const doc = this.shippingDoc;
      if (!doc) return null;
      const pps = doc.productandpriceshipper;
      const blocks = Array.isArray(pps)
        ? pps.map((b) => ({
            ...b,
            products: Array.isArray(b.products) ? b.products.map((p) => ({ ...p })) : [],
          }))
        : [];
      for (const row of this.editableRows) {
        const block = blocks[row.blockIndex];
        if (!block || !Array.isArray(block.products)) continue;
        const p = block.products[row.productIndex];
        if (!p) continue;
        const nQty = parseQtyDraft(row.unitQtyDraft);
        if (Number.isNaN(nQty)) {
          const err = new Error(
            this.$t("financialSummary.stages.shipPaymentsEditShipperInvoiceQtyInvalidNumber")
          );
          err.swalTitleKey = "financialSummary.stages.shipPaymentsEditShipperInvoiceQtyInvalidTitle";
          throw err;
        }
        const nPrice = parsePriceDraft(row.unitPriceDraft);
        if (Number.isNaN(nPrice)) {
          const err = new Error(
            this.$t("financialSummary.stages.shipPaymentsEditShipperPricesInvalidNumber")
          );
          err.swalTitleKey = "financialSummary.stages.shipPaymentsEditShipperPricesInvalidTitle";
          throw err;
        }
        const next = {
          ...p,
          unit_qty: nQty === null ? null : nQty,
          unit_price: nPrice === null ? null : nPrice,
        };
        next.product = productRefToId(p.product);
        block.products[row.productIndex] = next;
      }
      return blocks.map((block) => {
        const sanitized = this.sanitizeGroupBlockTopLevel(block);
        const { products, ...rest } = sanitized;
        const cleanProducts = (Array.isArray(products) ? products : []).map((p) => {
          const o = { ...p };
          o.product = productRefToId(p.product);
          return o;
        });
        return {
          ...rest,
          products: cleanProducts,
        };
      });
    },
    async submit() {
      if (!this.shippingId || !this.shippingDoc) return;
      let payload;
      try {
        payload = this.buildProductandpriceshipperPayload();
      } catch (err) {
        const titleKey =
          (err && err.swalTitleKey) ||
          "financialSummary.stages.shipPaymentsEditShipperInvoiceQtyInvalidTitle";
        await Swal.fire({
          icon: "warning",
          title: this.$tn(titleKey),
          text: err.message || this.$tn("error"),
        });
        return;
      }
      this.saving = true;
      try {
        await wencesApi.put(`/api/gotrading/dynamic/shipping/${this.shippingId}`, {
          partialUpdate: true,
          productandpriceshipper: payload,
        });
        await Swal.fire({
          icon: "success",
          title: this.$tn("financialSummary.stages.shipPaymentsEditShipperInvoiceQtySaved"),
          timer: 1800,
          showConfirmButton: false,
        });
        this.$emit("saved");
        this.close();
      } catch (e) {
        console.error("QuickShipperInvoiceUnitQtyModal submit:", e);
        const msg =
          (e.response && e.response.data && (e.response.data.message || e.response.data.error)) ||
          e.message ||
          "";
        await Swal.fire({
          icon: "error",
          title: this.$tn("error"),
          text: msg || this.$tn("financialSummary.stages.shipPaymentsEditShipperInvoiceQtySaveError"),
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.quick-shipper-invoice-qty-modal-root {
  position: fixed;
  inset: 0;
  z-index: 1055;
}
.quick-shipper-invoice-qty-modal-root .modal-backdrop {
  z-index: 1;
}
.quick-shipper-invoice-qty-modal-root .modal {
  display: block;
  position: relative;
  z-index: 2;
}
</style>
